var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useEffect } from "react";
import { colorStartHexa, generateAnalogousColors, generateComplementaryColors, generateDivergingColors, generateGoldenRatioColors, generateHSLBasedColors, generateMonochromaticColors, generatePastelColors, generateSequentialColors, generateTriadicColors, } from "./adminUtils";
import { useGetAdminColorsQuery, useSaveAdminColorsMutation, } from "@/store/api";
import { logger, useLogger } from "@/utils/loggerService";
var MIN_COLORS = 47;
/**
 * <b>Fonction appelée dans le widget,
  qui génère et enregistre une palette de couleurs dans la configuration si la clé n'existe pas encore,
  ou si le nombre de couleurs à générer est supérieur au nombre de couleurs déjà enregistrées (si la clé existe).</b>
 */
export function useGenerateDistinctColors(_a) {
    var numColors = _a.numColors, shade = _a.shade, colorBase = _a.colorBase, algo = _a.algo, endColor = _a.endColor, midColor = _a.midColor;
    var safeNumColors = Math.max(numColors !== null && numColors !== void 0 ? numColors : MIN_COLORS, MIN_COLORS);
    var _b = useState(), colors = _b[0], setColors = _b[1];
    var _c = useGetAdminColorsQuery({}), configColor = _c.data, isLoading = _c.isLoading;
    var _d = useSaveAdminColorsMutation(), saveThemeConfig = _d[0], _e = _d[1];
    var startColor = colorStartHexa(colorBase, shade);
    var colorsKey = "colors_palette_".concat(algo, "_").concat(shade);
    useEffect(function () {
        var _a, _b;
        setColors((_b = (_a = configColor === null || configColor === void 0 ? void 0 : configColor.data) === null || _a === void 0 ? void 0 : _a.colors_palette) === null || _b === void 0 ? void 0 : _b[colorsKey]);
    }, [configColor === null || configColor === void 0 ? void 0 : configColor.data]);
    var generateColors = function (algorithm, start) {
        switch (algorithm) {
            case "goldenRatio":
                return generateGoldenRatioColors(safeNumColors, start);
            case "hslBased":
                return generateHSLBasedColors(safeNumColors, start);
            case "pastel":
                return generatePastelColors(safeNumColors, start);
            case "analogous":
                return generateAnalogousColors(safeNumColors, start);
            case "complementary":
                return generateComplementaryColors(safeNumColors, start);
            case "triadic":
                return generateTriadicColors(safeNumColors, start);
            case "monochromatic":
                return generateMonochromaticColors(safeNumColors, start);
            case "sequential":
                return generateSequentialColors(safeNumColors, start, endColor || "#FFFFFF");
            case "diverging":
                return generateDivergingColors(safeNumColors, start, midColor || "#FFFFFF", endColor || "#000000");
            default:
                return generateGoldenRatioColors(safeNumColors, start);
        }
    };
    useEffect(function () {
        var _a;
        var _b;
        if (isLoading || !(configColor === null || configColor === void 0 ? void 0 : configColor.data))
            return;
        var existingPalette = (_b = configColor.data.colors_palette) !== null && _b !== void 0 ? _b : {};
        var existingColors = existingPalette[colorsKey];
        var mustRegenerate = !existingColors || existingColors.length !== safeNumColors;
        // ✅ Palette correcte → on utilise
        if (!mustRegenerate) {
            setColors(existingColors);
            return;
        }
        // 🔄 Régénération + normalisation
        var generatedColors = generateColors(algo, startColor);
        var updatedConfigs = __assign(__assign({}, configColor.data), { colors_palette: __assign(__assign({}, existingPalette), (_a = {}, _a[colorsKey] = generatedColors, _a)) });
        setColors(generatedColors);
        saveThemeConfig({ body: updatedConfigs })
            .unwrap()
            .then(function () {
            return logger.info("Palette ".concat(colorsKey, " normalis\u00E9e \u00E0 ").concat(safeNumColors, " couleurs"));
        })
            .catch(function (err) { return logger.error("Erreur sauvegarde palette : ".concat(err)); });
    }, [
        isLoading,
        configColor === null || configColor === void 0 ? void 0 : configColor.data,
        colorsKey,
        algo,
        startColor,
        safeNumColors,
    ]);
    return colors;
}
/**
 * <b>Fonction retournant une palette de couleurs en fonction du nombre de couleurs à générer,
 * de la valeur de l'intensité ou nuance la couleur, de la couleur de base,
 * de l'algorithme choisi, ainsi que des valeurs de la couleur intermédiaire
 * et de la couleur finale dans la palette.</b>
 */
function Generate(_a) {
    var numColors = _a.numColors, shade = _a.shade, colorBase = _a.colorBase, algo = _a.algo, endColor = _a.endColor, midColor = _a.midColor;
    var startColor = colorStartHexa(colorBase, shade);
    switch (algo) {
        case "goldenRatio":
            return generateGoldenRatioColors(numColors, startColor);
        case "hslBased":
            return generateHSLBasedColors(numColors, startColor);
        case "pastel":
            return generatePastelColors(numColors, startColor);
        case "analogous":
            return generateAnalogousColors(numColors, startColor);
        case "complementary":
            return generateComplementaryColors(numColors, startColor);
        case "triadic":
            return generateTriadicColors(numColors, startColor);
        case "monochromatic":
            return generateMonochromaticColors(numColors, startColor);
        case "sequential":
            return generateSequentialColors(numColors, startColor, endColor || "#FFFFFF");
        case "diverging":
            return generateDivergingColors(numColors, startColor, midColor || "#FFFFFF", endColor || "#000000");
        default:
            return generateGoldenRatioColors(numColors, startColor);
    }
}
/**
 * <b>Fonction utilisée lors du changement de la couleur de base,
 * qui régénère ou met à jour toutes les couleurs déjà générées dans la configuration.</b>
 */
export function useGenerateColors(_a) {
    var colorBase = _a.colorBase, endColor = _a.endColor, midColor = _a.midColor;
    var _b = useState({}), configs = _b[0], setConfigs = _b[1];
    var configColor = useGetAdminColorsQuery({}).data;
    var logger = useLogger().logger;
    useEffect(function () {
        if (configColor) {
            setConfigs(configColor.data);
        }
    }, [configColor]);
    if (!(configs === null || configs === void 0 ? void 0 : configs.colors_palette))
        return;
    var colorPaletteKeys = Object.keys(configs.colors_palette);
    var parsedKeysWithSize = colorPaletteKeys.map(function (key) {
        var _a, _b, _c;
        var _d = key.split("_"), algo = _d[2], shade = _d[3];
        var numColors = (_c = (_b = (_a = configs === null || configs === void 0 ? void 0 : configs.colors_palette) === null || _a === void 0 ? void 0 : _a[key]) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
        return { algo: algo, shade: shade, numColors: numColors };
    });
    var updatedPalettes = __assign({}, configs.colors_palette);
    parsedKeysWithSize.forEach(function (_a) {
        var algo = _a.algo, shade = _a.shade, numColors = _a.numColors;
        var colorsKey = "colors_palette_".concat(algo, "_").concat(shade);
        var generatedColors = Generate({
            numColors: numColors,
            shade: shade,
            colorBase: colorBase,
            algo: algo,
            endColor: endColor,
            midColor: midColor,
        });
        updatedPalettes[colorsKey] = generatedColors;
    });
    var updatedConfigs = __assign(__assign({}, configs), { colors_palette: updatedPalettes });
    return updatedConfigs;
}
/**
 * <b>Fonction utilisée lors du changement de la valeur de la couleur intermédiaire et de la couleur finale,
 * qui régénère ou met à jour toutes les couleurs utilisant l'algorithme diverging
 * déjà enregistrées dans la configuration.</b>
 *
 */
export function useRegenerateDiverging(_a) {
    var _b;
    var colorBase = _a.colorBase, endColor = _a.endColor, midColor = _a.midColor;
    var configColor = useGetAdminColorsQuery({}).data;
    var DEFAULT_NUM_COLORS = 47;
    var DEFAULT_SHADE = "400";
    if (!((_b = configColor === null || configColor === void 0 ? void 0 : configColor.data) === null || _b === void 0 ? void 0 : _b.colors_palette)) {
        return {};
    }
    var colorsPalette = configColor.data.colors_palette;
    var divergingKeys = Object.keys(colorsPalette).filter(function (key) {
        return key.includes("diverging");
    });
    var updatedDivergingPalettes = {};
    // 🔹 CAS 1 : il existe déjà des palettes diverging → update
    if (divergingKeys.length > 0) {
        divergingKeys.forEach(function (key) {
            var _a, _b;
            var _c = key.split("_"), shade = _c[3];
            var startColor = colorStartHexa(colorBase, shade);
            var numColors = (_b = (_a = colorsPalette[key]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : DEFAULT_NUM_COLORS;
            updatedDivergingPalettes[key] = generateDivergingColors(numColors, startColor, midColor || "#FFFFFF", endColor || "#000000");
        });
    }
    // 🔹 CAS 2 : aucune palette diverging → génération
    else {
        var startColor = colorStartHexa(colorBase, DEFAULT_SHADE);
        var newKey = "colors_palette_diverging_".concat(DEFAULT_SHADE);
        updatedDivergingPalettes[newKey] = generateDivergingColors(DEFAULT_NUM_COLORS, startColor, midColor || "#FFFFFF", endColor || "#000000");
    }
    return {
        colors_palette: updatedDivergingPalettes,
    };
}
/**
 * <b>Fonction utilisée lors du changement de la valeur de la couleur finale,
 *qui régénère ou met à jour toutes les couleurs utilisant l'algorithme sequential
 *déjà enregistrées dans la configuration.</b>
 */
export function useRegenerateSequential(_a) {
    var _b;
    var colorBase = _a.colorBase, endColor = _a.endColor;
    var configColor = useGetAdminColorsQuery({}).data;
    var DEFAULT_SHADE = "400";
    var DEFAULT_NUM_COLORS = 47;
    if (!((_b = configColor === null || configColor === void 0 ? void 0 : configColor.data) === null || _b === void 0 ? void 0 : _b.colors_palette)) {
        return {};
    }
    var colorsPalette = configColor.data.colors_palette;
    var sequentialKeys = Object.keys(colorsPalette).filter(function (key) {
        return key.includes("sequential");
    });
    var updatedSequentialPalettes = {};
    // 🔹 CAS 1 : palettes sequential existantes → mise à jour
    if (sequentialKeys.length > 0) {
        sequentialKeys.forEach(function (key) {
            var _a, _b;
            var _c = key.split("_"), shade = _c[3];
            var startColor = colorStartHexa(colorBase, shade);
            var numColors = (_b = (_a = colorsPalette[key]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : DEFAULT_NUM_COLORS;
            updatedSequentialPalettes[key] = generateSequentialColors(numColors, startColor, endColor || "#000000");
        });
    }
    // 🔹 CAS 2 : aucune palette sequential → génération par défaut
    else {
        var startColor = colorStartHexa(colorBase, DEFAULT_SHADE);
        var newKey = "colors_palette_sequential_".concat(DEFAULT_SHADE);
        updatedSequentialPalettes[newKey] = generateSequentialColors(DEFAULT_NUM_COLORS, startColor, endColor || "#000000");
    }
    return {
        colors_palette: updatedSequentialPalettes,
    };
}
